/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useState } from 'react'
import { Box, FormLabel, MenuItem, Select, Checkbox, FormControlLabel, Typography, type SelectProps } from '@mui/material'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import { grey } from '../../../../colors'
import { ButtonType } from '../../../../components/GenericButton/styles'
import GenericModal from '../../../../components/GenericModal'
import { getCurrentRole } from '../../../../helpers/roleHelpers'
import { useUserProfile } from '../../../../hooks/useUserProfile'
import { type ReportAccessLevel } from '../../../../APIManager/UserManager'

interface BulkExportModalProps {
  isOpen: boolean
  onClose: () => void
  onExport?: (exportConfig: ExportConfig) => void
  isExporting?: boolean
  disableOption?: boolean
  allowCommentsExport?: boolean
  initialExportBy?: string
  lockExportBy?: boolean
  allowByInstructor?: boolean
  allowIndividualResponses?: boolean
  /** False under `view_results_only` — CSV stays available for Comments Export (a different export product) regardless. */
  allowResultsCsvExport?: boolean
  /** The caller's resolved report-access level for the report currently on screen — `full_access` (explicit or unconfigured) additionally unlocks Individual Responses for non-global roles, matching the backend's export_by=responses gate. */
  reportAccessLevel?: ReportAccessLevel | null
}

export interface ExportConfig {
  exportBy: string
  organizeBy: string
  fileType: string
  exportWithReportInsights: boolean
  mergePDFs: boolean
  shouldIncludeByInstructor?: boolean
  shouldIncludeByReportingHierarchy?: boolean
  shouldIncludeMergePdf?: boolean
}

interface FormType {
  exportBy: string
  fileType: string
  exportWithReportInsights: boolean
  mergePDFs: boolean
}

const exportOptionCopy: Record<string, { label: string, description: string }> = {
  'Aggregate Report': {
    label: 'Aggregate Report',
    description: 'Survey results under this level, aggregated together.'
  },
  'by Course Section': {
    label: 'Course section by Hierarchy',
    description: 'Course section results under this level, grouped by the academic units in the reporting hierarchy.'
  },
  'by Instructor': {
    label: 'Course section by Instructor',
    description: 'Course section results under this level, grouped by instructor.'
  },
  'Comments Export': {
    label: 'Comments Export',
    description: 'Responses to all free-response questions in this survey under this level.'
  },
  'Individual Responses': {
    label: 'Individual Responses',
    description: 'All responses to the survey under this level, anonymized, with each row as one response rather than one student.'
  }
}

const exportMenuItemSx = {
  alignItems: 'flex-start',
  whiteSpace: 'normal',
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box'
} as const

const ExportOptionContent = ({ value }: { value: string }): JSX.Element => {
  const option = exportOptionCopy[value]
  if (option == null) {
    return <Typography>{value}</Typography>
  }
  return (
    <Box sx={{ py: 0.5, width: '100%', minWidth: 0 }}>
      <Typography sx={{ fontSize: '16px', lineHeight: '24px', color: grey[950], whiteSpace: 'normal' }}>{option.label}</Typography>
      <Typography sx={{ fontSize: '14px', lineHeight: '20px', color: grey[650], whiteSpace: 'normal' }}>{option.description}</Typography>
    </Box>
  )
}

type SelectDisplayProps = NonNullable<SelectProps['SelectDisplayProps']>

/* @mui Select test ids: React 17 HTMLAttributes typings omit data-testid */
/* eslint-disable @typescript-eslint/consistent-type-assertions -- narrow to MUI SelectDisplayProps */
const exportBySelectDisplayProps = { 'data-testid': 'bulk-export-export-by' } as SelectDisplayProps
const fileTypeSelectDisplayProps = { 'data-testid': 'bulk-export-file-type' } as SelectDisplayProps
/* eslint-enable @typescript-eslint/consistent-type-assertions */

const BulkExportModal: React.FC<BulkExportModalProps> = ({
  isOpen,
  onClose,
  onExport,
  isExporting = false,
  disableOption = false,
  allowCommentsExport = false,
  initialExportBy = 'Aggregate Report',
  lockExportBy = false,
  allowByInstructor = true,
  allowIndividualResponses = true,
  allowResultsCsvExport = true,
  reportAccessLevel = null
}) => {
  const methods = useForm<FormType>({
    defaultValues: {
      exportBy: 'Aggregate Report',
      fileType: 'PDF',
      exportWithReportInsights: false,
      mergePDFs: true
    }
  })

  const { handleSubmit, watch, reset, getValues } = methods
  const [exportMenuWidth, setExportMenuWidth] = useState<number | undefined>(undefined)
  const exportBy = watch('exportBy')
  const fileType = watch('fileType')

  const isIndividualResponses = exportBy === 'Individual Responses'
  const isCommentsExport = exportBy === 'Comments Export'
  const showMergePdfs = exportBy === 'by Course Section' || exportBy === 'by Instructor'
  const isCSVFileType = fileType === 'CSV'
  // Individual Responses / Comments Export always force CSV regardless of allowResultsCsvExport —
  // only the survey-results flows (Aggregate / by Course Section / by Instructor) are gated by it.
  const forcesCsvFileType = isIndividualResponses || isCommentsExport
  const showCsvFileTypeOption = allowResultsCsvExport || forcesCsvFileType

  const { data: userProfile } = useUserProfile()
  const currentUserRoles = userProfile?.roles ?? []
  const currentRole = getCurrentRole(currentUserRoles)
  // Individual Responses (raw student responses) is gated to GLOBAL roles, or — per the backend's
  // export_by=responses check — a role explicitly configured Full Access on this report. An
  // unconfigured (default) role does not qualify, matching the backend gate exactly: unconfigured
  // and explicit full_access both resolve reportAccessLevel to 'full_access' everywhere else in
  // this system, but this endpoint omits the X-Report-Access header entirely for the unconfigured
  // case specifically so this check can't accidentally include it.
  const canSeeIndividualResponses = currentRole?.roleName === 'Account Owner' || currentRole?.roleName === 'System Manager' || reportAccessLevel === 'full_access'

  const onSubmit = (data: FormType): void => {
    if (onExport) {
      const isByCourseSection = data.exportBy === 'by Course Section'
      const exportConfig: ExportConfig = {
        exportBy: data.exportBy,
        organizeBy: 'Reporting Hierarchy',
        fileType: data.fileType,
        exportWithReportInsights: data.exportWithReportInsights,
        mergePDFs: data.mergePDFs,
        shouldIncludeByInstructor: false,
        shouldIncludeByReportingHierarchy: isByCourseSection,
        shouldIncludeMergePdf: isByCourseSection && data.mergePDFs
      }
      onExport(exportConfig)
    }
  }

  const handleClose = (): void => {
    // Reset form state when closing; Merge PDFs keeps the user's last choice (CR-433)
    reset({
      exportBy: initialExportBy,
      fileType: initialExportBy === 'Comments Export' ? 'CSV' : 'PDF',
      exportWithReportInsights: false,
      mergePDFs: getValues('mergePDFs')
    })
    onClose()
  }

  useEffect(() => {
    if (!isOpen) {
      return
    }

    reset({
      exportBy: initialExportBy,
      fileType: initialExportBy === 'Comments Export' ? 'CSV' : 'PDF',
      exportWithReportInsights: false,
      mergePDFs: getValues('mergePDFs')
    })
  }, [getValues, initialExportBy, isOpen, reset])

  // Watch exportBy changes to auto-set fileType to CSV for Individual Responses / Comments Export
  useEffect(() => {
    if ((exportBy === 'Individual Responses' || exportBy === 'Comments Export') && fileType !== 'CSV') {
      methods.setValue('fileType', 'CSV')
    }
  }, [exportBy, fileType, methods])

  // A prior Comments Export selection can leave fileType stranded on 'CSV' after switching
  // to a survey-results flow where CSV is disallowed under view_results_only. fileType is
  // deliberately excluded from deps — this effect only needs to react to the two gating
  // inputs changing; including the value it also writes would cause a redundant re-run
  // (the guard sets fileType to 'PDF', which re-triggers the effect for a no-op second pass).
  useEffect(() => {
    if (!allowResultsCsvExport && !forcesCsvFileType && methods.getValues('fileType') === 'CSV') {
      methods.setValue('fileType', 'PDF')
    }
  }, [allowResultsCsvExport, forcesCsvFileType, methods])

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      confirmActionText="Export"
      confirmButtonLoading={isExporting}
      confirmActionType={ButtonType.Primary}
      onConfirm={methods.handleSubmit(onSubmit)}
      showCloseIcon={false}
    >
      <FormProvider {...methods}>
        <Box component="form">
          <Box display="flex" flexDirection="column" gap={4}>
            {/* Export By Section */}
            <Box>
              <FormLabel sx={{ fontWeight: 500, color: grey[950], fontSize: '16px', mb: 1, display: 'block' }}>Export</FormLabel>
              <Controller
                name="exportBy"
                control={methods.control}
                render={({ field }) => (
                  <Select
                    sx={{ paddingLeft: '0px' }}
                    fullWidth
                    size="small"
                    {...field}
                    IconComponent={KeyboardArrowDownRoundedIcon}
                    disabled={disableOption || lockExportBy}
                    SelectDisplayProps={exportBySelectDisplayProps}
                    renderValue={(selected) => exportOptionCopy[String(selected)]?.label ?? String(selected)}
                    onOpen={(event) => {
                      const field = event.currentTarget.parentElement
                      const width = field?.clientWidth ?? 0
                      if (width > 0) {
                        setExportMenuWidth(width)
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: exportMenuWidth == null
                          ? undefined
                          : { width: exportMenuWidth, maxWidth: exportMenuWidth, boxSizing: 'border-box' }
                      }
                    }}
                  >
                    <MenuItem value="Aggregate Report" sx={exportMenuItemSx}>
                      <ExportOptionContent value="Aggregate Report" />
                    </MenuItem>
                    {allowCommentsExport && (
                      <MenuItem value="Comments Export" sx={exportMenuItemSx}>
                        <ExportOptionContent value="Comments Export" />
                      </MenuItem>
                    )}
                    <MenuItem value="by Course Section" disabled={disableOption} sx={exportMenuItemSx}>
                      <ExportOptionContent value="by Course Section" />
                    </MenuItem>
                    {allowByInstructor && (
                      <MenuItem value="by Instructor" disabled={disableOption} sx={exportMenuItemSx}>
                        <ExportOptionContent value="by Instructor" />
                      </MenuItem>
                    )}
                    {canSeeIndividualResponses && allowIndividualResponses && (
                      <MenuItem value="Individual Responses" disabled={disableOption} sx={exportMenuItemSx}>
                        <ExportOptionContent value="Individual Responses" />
                      </MenuItem>
                    )}
                  </Select>
                )}
              />
            </Box>

            {/* File Type Section */}
            <Box>
              <FormLabel sx={{ fontWeight: 500, color: grey[950], fontSize: '16px', mb: 1, display: 'block' }}>File Type</FormLabel>
              <Controller
                name="fileType"
                control={methods.control}
                render={({ field }) => (
                  <Select
                    sx={{ paddingLeft: '0px' }}
                    fullWidth
                    size="small"
                    {...field}
                    disabled={isIndividualResponses || isCommentsExport}
                    IconComponent={KeyboardArrowDownRoundedIcon}
                    SelectDisplayProps={fileTypeSelectDisplayProps}
                  >
                    <MenuItem value="PDF">PDF</MenuItem>
                    {showCsvFileTypeOption && <MenuItem value="CSV">CSV</MenuItem>}
                  </Select>
                )}
              />
            </Box>

            {isCommentsExport && (
              <Typography sx={{ color: grey[650], fontSize: '14px', lineHeight: '20px' }}>
                Note: Exported free response questions are delivered by email.
              </Typography>
            )}

            {/* Merge PDFs Checkbox */}
            {showMergePdfs && !isCSVFileType && (
              <Controller
                name="mergePDFs"
                control={methods.control}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{ paddingLeft: '0px' }}
                        checked={value}
                        onChange={(e) => { onChange(e.target.checked) }}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 500, fontSize: '16px', color: grey[950] }}>
                          Merge PDFs
                        </Typography>
                        <Typography sx={{ color: grey[650], fontSize: '16px', mt: 0.5, lineHeight: '24px' }}>
                          Course section survey results will be grouped into a single PDF at the lowest level (Instructor or Course).
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', marginLeft: 0, marginRight: 0 }}
                  />
                )}
              />
            )}
          </Box>
        </Box>
      </FormProvider>
    </GenericModal>
  )
}

/**
 * Converts an ExportConfig into the API query params that both instructor export
 * paths (DistributionManager and UserManager) share: export_by and merge_pdf.
 */
export const buildExportConfigParams = (config: ExportConfig): { export_by?: string, merge_pdf?: string } => {
  const params: { export_by?: string, merge_pdf?: string } = {}
  if (config.exportBy === 'by Course Section') params.export_by = 'section'
  if (config.shouldIncludeMergePdf) params.merge_pdf = 'true'
  return params
}

export const INSTRUCTOR_BULK_EXPORT_MODAL_PROPS = {
  allowCommentsExport: false,
  allowByInstructor: false,
  allowIndividualResponses: false
} as const

export default BulkExportModal
